import { api } from "./apiClient";

export const inspectionsAPI = {
  listByProject: (projectId) => api.get(`/projects/${projectId}/inspections`),

  create: (projectId, payload) =>
    api.post(`/projects/${projectId}/inspections`, payload),

  getOne: (projectId, inspectionId) =>
    api.get(`/projects/${projectId}/inspections/${inspectionId}`),

  update: (projectId, inspectionId, payload) =>
    api.patch(`/projects/${projectId}/inspections/${inspectionId}`, payload),

  listQaQcModifications: (projectId, inspectionId) =>
    api.get(
      `/projects/${projectId}/inspections/${inspectionId}/qa-qc-modifications`,
    ),

  submit: (projectId, inspectionId) =>
    api.post(`/projects/${projectId}/inspections/${inspectionId}/submit`),

  approve: (projectId, inspectionId) =>
    api.post(`/projects/${projectId}/inspections/${inspectionId}/approve`),

  reject: (projectId, inspectionId, payload) =>
    api.post(
      `/projects/${projectId}/inspections/${inspectionId}/reject`,
      payload,
    ),

  reopen: (projectId, inspectionId) =>
    api.post(`/projects/${projectId}/inspections/${inspectionId}/reopen`),

  getChecklist: (projectId, inspectionId) =>
    api.get(`/projects/${projectId}/inspections/${inspectionId}/checklist`),

  seedChecklist: (projectId, inspectionId, payload) =>
    api.post(
      `/projects/${projectId}/inspections/${inspectionId}/checklist/seed`,
      payload,
    ),

  bulkUpdateChecklistItems: (projectId, inspectionId, updates) =>
    api.patch(
      `/projects/${projectId}/inspections/${inspectionId}/checklist/items/bulk`,
      { items: updates },
    ),

  completeChecklist: (projectId, inspectionId, payload) =>
    api.post(
      `/projects/${projectId}/inspections/${inspectionId}/checklist/complete`,
      payload,
    ),

  delete: (projectId, inspectionId) =>
    api.delete(`/projects/${projectId}/inspections/${inspectionId}`),

  listFindings: (projectId, inspectionId) =>
    api.get(`/projects/${projectId}/inspections/${inspectionId}/findings`),

  createFinding: (projectId, inspectionId, payload) =>
    api.post(
      `/projects/${projectId}/inspections/${inspectionId}/findings`,
      payload,
    ),

  updateFinding: (projectId, inspectionId, findingId, payload) =>
    api.patch(
      `/projects/${projectId}/inspections/${inspectionId}/findings/${findingId}`,
      payload,
    ),

  closeFinding: (projectId, inspectionId, findingId, payload = {}) =>
    api.post(
      `/projects/${projectId}/inspections/${inspectionId}/findings/${findingId}/close`,
      payload,
    ),

  deleteFinding: (projectId, inspectionId, findingId) =>
    api.delete(
      `/projects/${projectId}/inspections/${inspectionId}/findings/${findingId}`,
    ),

  getReportContext: (projectId, inspectionId) =>
    api
      .get(`/projects/${projectId}/inspections/${inspectionId}/report`)
      .then((res) => res.data),

  upsertReport: (projectId, inspectionId, payload) =>
    api.put(
      `/projects/${projectId}/inspections/${inspectionId}/report`,
      payload,
    ),

  downloadReportPdf: (projectId, inspectionId) =>
    api.get(`/projects/${projectId}/inspections/${inspectionId}/report/pdf`, {
      responseType: "blob",
      timeout: 110000,
    }),

  autofillWeather: (projectId, params) =>
    api.get(`/projects/${projectId}/inspections/weather/autofill`, {
      params,
    }),

  naSuggestions: (projectId, inspectionId, body = {}) =>
    api.post(
      `/projects/${projectId}/inspections/${inspectionId}/checklist/na-suggestions`,
      body,
    ),

  consistencyCheck: (projectId, inspectionId, body = {}) =>
    api.post(
      `/projects/${projectId}/inspections/${inspectionId}/checklist/consistency-check`,
      body,
    ),

  // RAG-based design (preview only)
  ragInspectionDesign(projectId, payload) {
    return api.post(`/projects/${projectId}/rag/inspection-design`, payload);
  },

  // Save approved design to project templates
  saveRagInspectionDesign(projectId, payload) {
    return api.post(
      `/projects/${projectId}/rag/inspection-design/save`,
      payload,
    );
  },

  // List project-specific inspection types (RAG + future manual)
  listProjectInspectionTypes(projectId) {
    return api.get(`/projects/${projectId}/inspection-types`);
  },

  // List checklist template items for a given type
  listProjectInspectionChecklistItems(projectId, inspectionTypeId) {
    return api.get(
      `/projects/${projectId}/inspection-types/${inspectionTypeId}/checklist-items`,
    );
  },
  // ---- Project inspection template CRUD ----

  createProjectInspectionType(projectId, payload) {
    return api.post(`/projects/${projectId}/inspection-types`, payload);
  },

  updateProjectInspectionType(projectId, inspectionTypeId, payload) {
    return api.patch(
      `/projects/${projectId}/inspection-types/${inspectionTypeId}`,
      payload,
    );
  },

  deleteProjectInspectionType(projectId, inspectionTypeId) {
    return api.delete(
      `/projects/${projectId}/inspection-types/${inspectionTypeId}`,
    );
  },

  createProjectInspectionChecklistItem(projectId, inspectionTypeId, payload) {
    return api.post(
      `/projects/${projectId}/inspection-types/${inspectionTypeId}/checklist-items`,
      payload,
    );
  },

  updateProjectInspectionChecklistItem(
    projectId,
    inspectionTypeId,
    itemId,
    payload,
  ) {
    return api.patch(
      `/projects/${projectId}/inspection-types/${inspectionTypeId}/checklist-items/${itemId}`,
      payload,
    );
  },

  deleteProjectInspectionChecklistItem(projectId, inspectionTypeId, itemId) {
    return api.delete(
      `/projects/${projectId}/inspection-types/${inspectionTypeId}/checklist-items/${itemId}`,
    );
  },
};

// Client assigned inspections workflow
inspectionsAPI.listMyAssignedInspections = (projectId) =>
  api.get(`/projects/${projectId}/my-assigned-inspections`);

// Client assigned findings workflow
inspectionsAPI.listMyAssignedFindings = (projectId) =>
  api.get(`/projects/${projectId}/my-assigned-findings`);

inspectionsAPI.submitFindingResponse = (
  projectId,
  inspectionId,
  findingId,
  payload,
) =>
  api.post(
    `/projects/${projectId}/inspections/${inspectionId}/findings/${findingId}/response`,
    payload,
  );

inspectionsAPI.listFindingComments = (projectId, inspectionId, findingId) =>
  api.get(
    `/projects/${projectId}/inspections/${inspectionId}/findings/${findingId}/comments`,
  );

inspectionsAPI.getDocumentDownloadUrl = (projectId, documentId) =>
  api.get(`/projects/${projectId}/documents/${documentId}/download-url`);

inspectionsAPI.reopenFinding = (projectId, inspectionId, findingId, payload) =>
  api.post(
    `/projects/${projectId}/inspections/${inspectionId}/findings/${findingId}/reopen`,
    payload,
  );

// Inspection assignment workflow
inspectionsAPI.listInspectionAssignments = (projectId, inspectionId) =>
  api.get(`/projects/${projectId}/inspections/${inspectionId}/assignments`);

inspectionsAPI.assignInspection = (projectId, inspectionId, payload) =>
  api.post(
    `/projects/${projectId}/inspections/${inspectionId}/assignments`,
    payload,
  );

inspectionsAPI.deleteInspectionAssignment = (
  projectId,
  inspectionId,
  assignmentId,
) =>
  api.delete(
    `/projects/${projectId}/inspections/${inspectionId}/assignments/${assignmentId}`,
  );

// Backward-compatible alias for older My Issues components that only passed findingId.
// Prefer submitFindingResponse(projectId, inspectionId, findingId, payload) when inspection_id is available.
inspectionsAPI.respondAssignedFinding = (projectId, findingId, payload) =>
  api.post(
    `/projects/${projectId}/my-assigned-findings/${findingId}/response`,
    payload,
  );

// Generalized issue review workflow
inspectionsAPI.listFindingReviews = (projectId, findingId) =>
  api.get(`/projects/${projectId}/findings/${findingId}/reviews`);

inspectionsAPI.reviewFinding = (projectId, findingId, payload) =>
  api.post(`/projects/${projectId}/findings/${findingId}/review`, payload);

// Organization-aware issue escalation workflow
inspectionsAPI.getFindingEscalationTargets = (projectId, findingId) =>
  api.get(`/projects/${projectId}/findings/${findingId}/escalation-targets`);

inspectionsAPI.listFindingEscalations = (projectId, findingId) =>
  api.get(`/projects/${projectId}/findings/${findingId}/escalations`);

inspectionsAPI.createFindingEscalation = (projectId, findingId, payload) =>
  api.post(`/projects/${projectId}/findings/${findingId}/escalations`, payload);

inspectionsAPI.listEngineeringIssues = (projectId) =>
  api.get(`/projects/${projectId}/engineering-issues`);

inspectionsAPI.answerFindingEscalation = (projectId, escalationId, payload) =>
  api.patch(`/projects/${projectId}/escalations/${escalationId}/answer`, payload);

inspectionsAPI.listEscalationInbox = (projectId) =>
  api.get(`/projects/${projectId}/escalation-inbox`);

inspectionsAPI.forwardFindingEscalation = (projectId, escalationId, payload) =>
  api.post(`/projects/${projectId}/escalations/${escalationId}/forward`, payload);

// Lightweight Supervisor / Issue Control reads.
// These keep the initial dashboard request small and load issue context only on demand.
inspectionsAPI.getSupervisorDashboardSummary = (projectId) =>
  api.get(`/projects/${projectId}/supervisor/dashboard-summary`);

inspectionsAPI.listSupervisorFindingsPage = (projectId, { offset = 0, limit = 100 } = {}) =>
  api.get(`/projects/${projectId}/supervisor/foreman-findings-page`, {
    params: { offset, limit },
  });

inspectionsAPI.getSupervisorFindingContext = (projectId, findingId) =>
  api.get(`/projects/${projectId}/supervisor/findings/${findingId}/context`);

inspectionsAPI.getIssueVisibilityAudit = (projectId) =>
  api.get(`/projects/${projectId}/admin/issue-visibility-audit`);
